import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { deleteGiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.controller';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { GiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Link } from 'react-router-dom';

export const GiftItemBrandListPage: FC = function GiftItemBrandListPage() {
  const { sellerRole } = useAuth();

  useGiftItemBrandListHook(sellerRole());

  const status = useRecoilValue(giftItemBrandListStatusAtom);
  const data = useRecoilValue(giftItemBrandListDataAtom);
  const setData = useSetRecoilState(giftItemBrandListDataAtom);

  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase().trim();
    return data.filter((item) => item.name.toLowerCase().includes(query));
  }, [data, searchQuery]);

  // 삭제 핸들러
  const handleDelete = useCallback(
    async (brand: GiftItemBrand) => {
      if (!confirm(`"${brand.name}" 브랜드를 삭제하시겠습니까?`)) return;

      try {
        setDeletingId(brand._id);
        await deleteGiftItemBrand(brand._id, sellerRole());
        // 목록에서 제거
        setData((prev) => prev.filter((item) => item._id !== brand._id));
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData],
  );

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {searchQuery ? '검색 결과가 없습니다.' : '등록된 상품 브랜드가 없습니다.'}
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {filteredData.map((item) => (
            <div
              key={item._id}
              className="flex items-center justify-between rounded-lg border bg-white p-4 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                  <span className="text-lg font-bold">{item.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-medium">{item.name}</p>
                  {item.createdAt && (
                    <p className="text-xs text-gray-400">
                      {new Date(item.createdAt).toLocaleDateString('ko-KR')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/gift-seller/item-brand/${item._id}/edit`}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                >
                  수정
                </Link>
                <button
                  onClick={() => handleDelete(item)}
                  disabled={deletingId === item._id}
                  className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {deletingId === item._id ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [status, filteredData, searchQuery, deletingId, handleDelete]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">상품 브랜드 관리</h1>
          <Link to="/gift-seller/item-brand/new">
            <Button className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white">
              + 브랜드 등록
            </Button>
          </Link>
        </div>

        {/* 통계 */}
        {status === 'success' && (
          <div className="mt-3 text-sm text-gray-600">
            전체 <span className="font-medium text-gray-900">{data?.length || 0}</span>개
          </div>
        )}
      </div>

      {/* 검색 */}
      <div className="border-b bg-white px-6 py-3">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="브랜드명 검색..."
            className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            🔍
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        {/* 검색 결과 수 */}
        {status === 'success' && searchQuery && (
          <div className="mt-2 text-xs text-gray-500">
            {filteredData.length}개 표시 (전체 {data?.length || 0}개 중)
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
